import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../types/api';
import { acceptModuleTerms, getModuleTerms, moduleForPath, type ModuleTerms } from '../../services/moduleTerms';

/**
 * Asks each user to accept a module's terms of use the first time they open a
 * page of that module (and again when the terms change). Only users with
 * access to the module are asked — the server decides that. Mounted once in
 * App; the page underneath stays rendered.
 *
 * Fails open, like PlatformTermsGate: if the list cannot be loaded nothing is
 * shown, so a worker offline in a paddock is never locked out of clock-in.
 * Spray has its own gate (SprayTncGate), with server-side enforcement.
 */
function ModuleTermsGate() {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryKey = ['module-terms', user?.id, user?.organisation_id];

  const moduleKey = moduleForPath(pathname);

  const { data: all } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await getModuleTerms();
      return res.success ? res.data : null;
    },
    enabled: isAuthenticated && !!user?.organisation_id,
    staleTime: Infinity,
    retry: false,
  });

  if (!isAuthenticated || !moduleKey || !all) return null;
  const terms = all.find((t) => t.module_key === moduleKey);
  if (!terms || !terms.hasAccess || terms.hasAccepted) return null;

  return (
    <ModuleTermsDialog
      terms={terms}
      onAccepted={() =>
        queryClient.setQueryData<ModuleTerms[] | null>(queryKey, (prev) =>
          prev ? prev.map((t) => (t.id === terms.id ? { ...t, hasAccepted: true } : t)) : prev,
        )
      }
      onStale={() => queryClient.invalidateQueries({ queryKey })}
      onLeave={() => navigate('/dashboard')}
    />
  );
}

interface DialogProps {
  terms: ModuleTerms;
  onAccepted: () => void;
  onStale: () => void;
  /** Not now: leave the module without accepting. */
  onLeave: () => void;
}

export function ModuleTermsDialog({ terms, onAccepted, onStale, onLeave }: DialogProps) {
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleAccept = async () => {
    if (!agreed) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await acceptModuleTerms(terms.module_key, terms.id);
      if (res.success) {
        onAccepted();
        return;
      }
      setError('Could not record your acceptance. Please try again.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('These terms were updated while you were reading. The latest version is loading.');
        onStale();
      } else {
        setError('Could not record your acceptance. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="module-terms-title"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <header className="px-4 sm:px-6 py-4 border-b border-neutral-200 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-primary-600 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <h2 id="module-terms-title" className="text-lg font-semibold text-neutral-900 break-words">
              {terms.title}
            </h2>
            <p className="text-xs text-neutral-500">Version {terms.version}</p>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4">
          {/* Admin-authored Markdown, pre-wrapped like the spray terms — no
              markdown library for a handful of screens. */}
          <pre className="whitespace-pre-wrap break-words font-sans text-sm text-neutral-700 leading-relaxed">
            {terms.content}
          </pre>
          <p className="mt-4 text-sm text-neutral-700">
            These apply alongside the{' '}
            <Link to="/terms" className="text-primary-600 hover:text-primary-700 font-medium">
              Terms and Conditions
            </Link>
            .
          </p>
        </div>

        <footer className="px-4 sm:px-6 py-4 border-t border-neutral-200 bg-neutral-50 rounded-b-xl space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-sm text-neutral-700">I have read and agree to these terms of use.</span>
          </label>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={onLeave} disabled={submitting}>
              Not now
            </Button>
            <Button onClick={handleAccept} disabled={!agreed || submitting} isLoading={submitting}>
              Accept and continue
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default ModuleTermsGate;
